import { API_URL } from './apiConfig';

// --- AUTHENTICATION ---
export const signup = async (firstName, lastName, email, password) => {
  const response = await fetch(`${API_URL}/signup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ firstName, lastName, email, password }),
  });
  if (!response.ok) {
    const data = await response.json();
    const error = new Error(data.message || 'Signup failed');
    error.data = data; // Attach full response data (like verified status)
    throw error;
  }
  return await response.json();
};

export const login = async (email, password) => {
  const response = await fetch(`${API_URL}/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.message || 'Login failed');
  }
  return await response.json();
};

export const logout = async (userId, email) => {
  try {
    await fetch(`${API_URL}/logout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, email }),
    });
  } catch (err) {
    console.warn('Logout logging failed', err);
  }
  localStorage.removeItem('user');
  localStorage.removeItem('cart');
};

export const verifyCode = async (email, code) => {
  const response = await fetch(`${API_URL}/verify-code`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code }),
  });
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.message || 'Verification failed');
  }
  return await response.json();
};

export const resendVerification = async (email) => {
  const response = await fetch(`${API_URL}/resend-verification`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.message || 'Failed to resend verification');
  }
  return await response.json();
};

// --- PROFILE ---
export const getProfile = async (userId) => {
  const response = await fetch(`${API_URL}/profile/${userId}`);
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.message || 'Failed to load profile');
  }
  return await response.json();
};

export const updateProfile = async (userId, profileData) => {
  const response = await fetch(`${API_URL}/profile`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, ...profileData }),
  });
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.message || 'Failed to update profile');
  }
  return await response.json();
};

export const requestPasswordOtp = async (userId, oldPassword) => {
  const response = await fetch(`${API_URL}/profile/request-password-otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, oldPassword }),
  });
  if (!response.ok) {
    const data = await response.json();
    const err = new Error(data.message || 'Failed to request code');
    if (data.lockoutSeconds) err.lockoutSeconds = data.lockoutSeconds;
    if (data.cooldownSeconds) err.cooldownSeconds = data.cooldownSeconds;
    throw err;
  }
  return await response.json();
};

export const verifyPasswordOtp = async (userId, otp) => {
  const response = await fetch(`${API_URL}/profile/verify-password-otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, otp }),
  });
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.message || 'Invalid code');
  }
  return await response.json();
};

export const changePassword = async (userId, otp, oldPassword, newPassword) => {
  const response = await fetch(`${API_URL}/profile/password`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, otp, oldPassword, newPassword }),
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.message || 'Failed to change password');
  }
  return await response.json();
};


export const requestIdentityOtp = async (userId) => {
  const response = await fetch(`${API_URL}/profile/request-identity-otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId }),
  });
  if (!response.ok) {
    const data = await response.json();
    const err = new Error(data.message || 'Failed to send identity verification code');
    if (data.lockoutSeconds) err.lockoutSeconds = data.lockoutSeconds;
    if (data.cooldownSeconds) err.cooldownSeconds = data.cooldownSeconds;
    throw err;
  }
  return await response.json();
};

export const verifyIdentityOtp = async (userId, otp) => {
  const response = await fetch(`${API_URL}/profile/verify-identity-otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, otp }),
  });
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.message || 'Invalid identity verification code');
  }
  return await response.json();
};

export const requestEmailOtp = async (userId, newEmail, identityToken) => {
  const response = await fetch(`${API_URL}/profile/request-email-otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, newEmail, identityToken }),
  });
  if (!response.ok) {
    const data = await response.json();
    const err = new Error(data.message || 'Failed to send verification code');
    if (data.lockoutSeconds) err.lockoutSeconds = data.lockoutSeconds;
    if (data.cooldownSeconds) err.cooldownSeconds = data.cooldownSeconds;
    throw err;
  }
  return await response.json();
};

export const verifyEmailOtp = async (userId, otp, identityToken) => {
  const response = await fetch(`${API_URL}/profile/verify-email-otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, otp, identityToken }),
  });
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.message || 'Invalid verification code');
  }
  return await response.json();
};


// --- ADDRESSES ---
const getAuthHeaders = () => {
  let token = localStorage.getItem('token') || '';
  if (!token) {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser);
        token = parsed.token || parsed.accessToken || '';
      } catch (_) {}
    }
  }
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
};

export const getAddresses = async (userId) => {
  if (!userId || userId === 'undefined') return [];
  try {
    const response = await fetch(`${API_URL}/address/${userId}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) return [];
    const json = await response.json();
    if (Array.isArray(json)) return json;
    return json.data || [];
  } catch (e) {
    console.warn('Error fetching addresses:', e);
    return [];
  }
};

export const addAddress = async (userId, addressData) => {
  const response = await fetch(`${API_URL}/address`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ userId, ...addressData }),
  });
  if (!response.ok) throw new Error('Failed to add address');
  return await response.json();
};

export const updateAddress = async (userId, addressId, addressData) => {
  const response = await fetch(`${API_URL}/address/${addressId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify({ userId, ...addressData }),
  });
  if (!response.ok) throw new Error('Failed to update address');
  return await response.json();
};

export const deleteAddress = async (userId, addressId) => {
  const response = await fetch(`${API_URL}/address/${addressId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
    body: JSON.stringify({ userId }),
  });
  if (!response.ok) throw new Error('Failed to delete address');
  return await response.json();
};

export const setDefaultAddress = async (userId, addressId) => {
  const response = await fetch(`${API_URL}/address/default/${addressId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify({ userId }),
  });
  if (!response.ok) throw new Error('Failed to set default address');
  return await response.json();
};

// --- CART ---
export const getCart = async (userId) => {
  const response = await fetch(`${API_URL}/cart/${userId}`);
  if (!response.ok) throw new Error('Failed to fetch cart');
  return await response.json();
};

export const syncCart = async (userId, items) => {
  const response = await fetch(`${API_URL}/cart/${userId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items }),
  });
  if (!response.ok) throw new Error('Failed to sync cart');
  return await response.json();
};

// --- ORDERS ---
export const createOrder = async (orderData) => {
  const response = await fetch(`${API_URL}/orders`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(orderData),
  });
  if (!response.ok) throw new Error('Failed to create order');
  return await response.json();
};

export const getOrders = async (userId) => {
  const response = await fetch(`${API_URL}/orders`);
  if (!response.ok) throw new Error('Failed to fetch orders');
  const allOrders = await response.json();
  // Filter by userId if provided
  if (userId) {
    return allOrders.filter(order => order.userId === userId);
  }
  return allOrders;
};

export const clearAllOrders = async () => {
  const response = await fetch(`${API_URL}/orders/clearAll`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`
    }
  });
  if (!response.ok) throw new Error('Failed to clear orders');
  return await response.json();
};

export const updateOrderStatus = async (orderId, status) => {
  let role = 'user';
  try {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const user = JSON.parse(storedUser);
      role = user.role || 'user';
    }
  } catch (e) {
    console.error('Error parsing user role:', e);
  }

  const response = await fetch(`${API_URL}/orders/${orderId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status, role }),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to update order status');
  }
  return await response.json();
};

export const cancelOrder = async (orderId, reason) => {
  let token = localStorage.getItem('token') || '';
  if (!token) {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser);
        token = parsed.token || parsed.accessToken || '';
      } catch (_) {}
    }
  }

  const response = await fetch(`${API_URL}/cancelOrder`, {
    method: 'POST',
    headers: { 
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : ''
    },
    body: JSON.stringify({ orderId, reason }),
  });
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.message || data.data || 'Failed to cancel order');
  }
  return await response.json();
};

// --- ADMIN ---
export const getAllUsers = async () => {
  const response = await fetch(`${API_URL}/users`);
  if (!response.ok) throw new Error('Failed to fetch users');
  return await response.json();
};

export const archiveUser = async (userId, isArchived) => {
  const response = await fetch(`${API_URL}/users/${userId}/archive`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ isArchived })
  });
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.message || 'Failed to archive user');
  }
  return await response.json();
};

// --- CUSTOM FURNITURE REQUESTS ---
export const createCustomRequest = async (requestData) => {
  const response = await fetch(`${API_URL}/custom-requests`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(requestData),
  });
  if (!response.ok) throw new Error('Failed to create custom request');
  return await response.json();
};

export const getCustomRequests = async () => {
  const response = await fetch(`${API_URL}/custom-requests`);
  if (!response.ok) throw new Error('Failed to fetch custom requests');
  return await response.json();
};

export const getUserCustomRequests = async (userId) => {
  const response = await fetch(`${API_URL}/custom-requests/${userId}`);
  if (!response.ok) throw new Error('Failed to fetch user requests');
  return await response.json();
};

export const updateCustomRequest = async (id, updates) => {
  const response = await fetch(`${API_URL}/custom-requests/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
  if (!response.ok) throw new Error('Failed to update request');
  return await response.json();
};

export const getAuditLogs = async () => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${API_URL}/audit-logs`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  if (!response.ok) throw new Error('Failed to fetch audit logs');
  const result = await response.json();
  return Array.isArray(result) ? result : (result.data || []);
};
export const createStaff = async (staffData) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${API_URL}/admin/create-account`, {
    method: 'POST',
    headers: { 
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(staffData),
  });
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.message || 'Failed to create staff account');
  }
  return await response.json();
};

export const checkPendingVerification = async (email) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${API_URL}/admin/check-pending-verification`, {
    method: 'POST',
    headers: { 
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ email }),
  });
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.message || 'Verification check failed');
  }
  return await response.json();
};

// --- NOTIFICATIONS ---
export const getNotifications = async (userIdOrRole) => {
  const response = await fetch(`${API_URL}/notifications/${userIdOrRole}`);
  if (!response.ok) throw new Error('Failed to fetch notifications');
  return await response.json();
};

export const markNotificationRead = async (notificationId) => {
  const response = await fetch(`${API_URL}/notifications/${notificationId}/read`, {
    method: 'PUT'
  });
  if (!response.ok) throw new Error('Failed to mark notification as read');
  return await response.json();
};

export const markAllNotificationsRead = async (userIdOrRole) => {
  const response = await fetch(`${API_URL}/notifications/read-all/${userIdOrRole}`, {
    method: 'PUT'
  });
  if (!response.ok) throw new Error('Failed to mark all notifications as read');
  return await response.json();
};

export const clearAllNotifications = async (userIdOrRole) => {
  const response = await fetch(`${API_URL}/notifications/clear-all/${userIdOrRole}`, {
    method: 'DELETE'
  });
  if (!response.ok) throw new Error('Failed to clear notifications');
  return await response.json();
};

export const getNotificationSettings = async (userId) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${API_URL}/user/${userId}/notification-settings`, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  if (!response.ok) throw new Error('Failed to fetch notification settings');
  return await response.json();
};

export const updateNotificationSettings = async (userId, settings) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${API_URL}/user/notification-settings`, {
    method: 'POST',
    headers: { 
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ userId, settings })
  });
  if (!response.ok) throw new Error('Failed to update notification settings');
  return await response.json();
};

// --- PRODUCTS ---
export const getProductById = async (productId) => {
  const response = await fetch(`${API_URL}/product/${productId}`);
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.message || 'Failed to fetch product');
  }
  return await response.json();
};

export const getAllProductsAdmin = async (filters = {}) => {
  const response = await fetch(`${API_URL}/getAllProductsAdmin`, {
    method: 'POST',
    headers: { 
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('token')}`
    },
    body: JSON.stringify(filters),
  });
  if (!response.ok) throw new Error('Failed to fetch admin products');
  const data = await response.json();
  return data;
};

export const getProducts = async (filters = {}) => {
  const response = await fetch(`${API_URL}/getProducts`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(filters),
  });
  if (!response.ok) throw new Error('Failed to fetch products');
  return await response.json();
};

export const deleteProduct = async (productId) => {
  const response = await fetch(`${API_URL}/deleteProduct`, {
    method: 'POST',
    headers: { 
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('token')}`
    },
    body: JSON.stringify({ productId }),
  });
  if (!response.ok) throw new Error('Failed to delete product');
  return await response.json();
};

export const addProduct = async (productData) => {
  const response = await fetch(`${API_URL}/addProduct`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('token')}`
    },
    body: JSON.stringify(productData),
  });
  if (!response.ok) throw new Error('Failed to add product');
  return await response.json();
};

export const updateProduct = async (productId, productData) => {
  const response = await fetch(`${API_URL}/updateProduct`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('token')}`
    },
    body: JSON.stringify({ productId, ...productData }),
  });
  if (!response.ok) throw new Error('Failed to update product');
  const data = await response.json();
  if (data.status === 'error') throw new Error(data.message || 'Failed to update product');
  return data;
};

export const getProductManagementBootstrap = async () => {
  const response = await fetch(`${API_URL}/product-management-bootstrap?t=${Date.now()}`, {
    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
  });
  if (!response.ok) throw new Error('Failed to fetch bootstrap data');
  return await response.json();
};

// --- SALES ---
export const getSalesDashboard = async () => {
  const response = await fetch(`${API_URL}/sales-dashboard`, {
    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
  });
  if (!response.ok) throw new Error('Failed to fetch sales dashboard');
  return await response.json();
};

// --- INVENTORY ---
export const getInventory = async () => {
  const response = await fetch(`${API_URL}/inventory`, {
    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
  });
  if (!response.ok) throw new Error('Failed to fetch inventory');
  return await response.json();
};

export const createInventoryMaterial = async (materialData) => {
  const response = await fetch(`${API_URL}/inventory`, {
    method: 'POST',
    headers: { 
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('token')}`
    },
    body: JSON.stringify(materialData),
  });
  if (!response.ok) throw new Error('Failed to create material');
  return await response.json();
};

export const updateInventoryMaterial = async (id, materialData) => {
  const response = await fetch(`${API_URL}/inventory/${id}`, {
    method: 'PUT',
    headers: { 
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('token')}`
    },
    body: JSON.stringify(materialData),
  });
  if (!response.ok) throw new Error('Failed to update material');
  return await response.json();
};

export const deleteInventoryMaterial = async (id) => {
  const response = await fetch(`${API_URL}/inventory/${id}`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
  });
  if (!response.ok) throw new Error('Failed to delete material');
  return await response.json();
};

export const processInventoryOrder = async (orderData) => {
  const response = await fetch(`${API_URL}/inventory/process-order`, {
    method: 'POST',
    headers: { 
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('token')}`
    },
    body: JSON.stringify(orderData),
  });
  if (!response.ok) throw new Error('Failed to process order');
  return await response.json();
};

export const upsertSupplier = async (supplierData) => {
  const response = await fetch(`${API_URL}/suppliers`, {
    method: 'POST',
    headers: { 
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('token')}`
    },
    body: JSON.stringify(supplierData),
  });
  if (!response.ok) throw new Error('Failed to save supplier');
  return await response.json();
};export const deleteSupplier = async (supplierId) => {
  const response = await fetch(`${API_URL}/suppliers/${supplierId}`, {
    method: 'DELETE',
    headers: { 
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('token')}`
    },
  });
  if (!response.ok) {
    const errData = await response.json().catch(() => ({}));
    throw new Error(errData.message || 'Failed to delete supplier');
  }
  return await response.json();
};


// --- EXTRA ADMIN ORDER FUNCTIONS ---
export const editOrderDetails = async (orderId, details) => {
  const response = await fetch(`${API_URL}/orders/${orderId}/edit-details`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(details),
  });
  if (!response.ok) throw new Error('Failed to edit order details');
  return await response.json();
};

export const updateCustomerProgress = async (orderId, message) => {
  const response = await fetch(`${API_URL}/orders/${orderId}/update-customer`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ message }),
  });
  if (!response.ok) throw new Error('Failed to send progress update');
  return await response.json();
};

export const paymongoRefund = async (orderId, reason) => {
  const storedUser = localStorage.getItem('user');
  let token = '';
  if (storedUser) {
    try {
      const parsed = JSON.parse(storedUser);
      token = parsed.token || '';
    } catch (_) {}
  }
  if (!token) {
    token = localStorage.getItem('token') || '';
  }

  const response = await fetch(`${API_URL}/paymongo/refund`, {
    method: 'POST',
    headers: { 
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ orderId, reason }),
  });
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.message || data.data || 'Failed to refund order');
  }
  return await response.json();
};

// --- REVIEWS ---
export const fetchToRateOrders = async () => {
  let token = '';
  try {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const parsed = JSON.parse(userStr);
      token = parsed.token || '';
    }
  } catch (_) {}
  if (!token) token = localStorage.getItem('token') || '';

  const response = await fetch(`${API_URL}/reviews/to-rate`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!response.ok) throw new Error('Failed to fetch to-rate orders');
  const data = await response.json();
  return data.data;
};

export const fetchMyReviews = async () => {
  let token = '';
  try {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const parsed = JSON.parse(userStr);
      token = parsed.token || '';
    }
  } catch (_) {}
  if (!token) token = localStorage.getItem('token') || '';

  const response = await fetch(`${API_URL}/reviews/my-reviews`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!response.ok) throw new Error('Failed to fetch my reviews');
  const data = await response.json();
  return data.data;
};

export const submitReview = async ({ orderId, rating, reviewText, photos, productName, variant, price, productImage }) => {
  let token = '';
  try {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const parsed = JSON.parse(userStr);
      token = parsed.token || '';
    }
  } catch (_) {}
  if (!token) token = localStorage.getItem('token') || '';

  // Handle photos: if any photo is a File (from input type="file"), upload it first
  const photoUrls = [];
  for (const photo of photos) {
    if (photo.file) {
      const formData = new FormData();
      formData.append('photo', photo.file);
      const uploadRes = await fetch(`${API_URL}/reviews/upload-photo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      if (uploadRes.ok) {
        const uploadData = await uploadRes.json();
        if (uploadData.data && uploadData.data.url) {
          photoUrls.push(uploadData.data.url);
        }
      }
    } else if (typeof photo === 'string') {
      photoUrls.push(photo);
    }
  }

  const response = await fetch(`${API_URL}/reviews/submit`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      orderId,
      rating,
      reviewText,
      photos: photoUrls,
      productName,
      variant,
      price,
      productImage,
    }),
  });
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.message || 'Failed to submit review');
  }
  return await response.json();
};

export const getProductReviews = async (productName) => {
  try {
    const response = await fetch(
      `${API_URL}/reviews/product/${encodeURIComponent(productName)}`
    );
    if (!response.ok) return [];
    const data = await response.json();
    return Array.isArray(data.data) ? data.data : (Array.isArray(data) ? data : []);
  } catch (_) {
    return [];
  }
};
